// Google AdSense — auto-ads loader with per-region consent gating.
//
// Behaviour:
//   1. Skip entirely when publisher ID env var is missing.
//   2. Skip on Premium fans (subscription_tier starts with "premium").
//   3. Skip on excluded routes (auth / admin / artist ops / cart / payment).
//   4. Load unconditionally for non-EU/UK visitors — matches product decision
//      from the AdSense onboarding thread.
//   5. For EU/UK/unknown-geo visitors, only load once the advertising consent
//      flag is granted. Re-checks on `CONSENT_EVENT`.
//
// Sets `window.__adsenseLoaded = true` so <ManualAdSlot> knows when it can
// push its slot.
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { CONSENT_EVENT } from '../utils/metaPixel';
import { getConsent } from '../utils/consent';

const CLIENT_ID = process.env.REACT_APP_ADSENSE_CLIENT_ID;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GEO_CACHE_KEY = 'gospeleon.geo';
const GEO_CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6h

const EXCLUDED_PREFIXES = [
  '/login', '/register', '/forgot-password', '/reset-password', '/verify-email',
  '/admin', '/artist-dashboard', '/artist-earnings', '/artist-analytics',
  '/payment/callback', '/cart',
];

function isExcludedPath(pathname) {
  return EXCLUDED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function isPremiumUser(user) {
  return (user?.subscription_tier || '').startsWith('premium');
}

async function fetchGeoHint() {
  try {
    const cached = JSON.parse(localStorage.getItem(GEO_CACHE_KEY) || 'null');
    if (cached && Date.now() - cached.t < GEO_CACHE_TTL_MS) return cached.data;
  } catch { /* ignore */ }
  try {
    const { data } = await axios.get(`${API}/geo/country`, { timeout: 4000 });
    try { localStorage.setItem(GEO_CACHE_KEY, JSON.stringify({ t: Date.now(), data })); } catch { /* ignore */ }
    return data;
  } catch {
    // Fail-safe: treat as consent-required so we don't accidentally serve to EU/UK
    return { country: 'XX', is_eu: false, is_uk: false, is_eu_uk: true, requires_ad_consent: true };
  }
}

function injectAdSenseScript() {
  if (document.querySelector('script[data-adsense-loader="true"]')) return;
  // Wrap the AdSense loader in a small marker <script> so we can locate it
  // without adding custom data-* attrs to Google's own tag (which triggers
  // console warnings on every load).
  const marker = document.createElement('script');
  marker.type = 'text/plain';
  marker.setAttribute('data-adsense-loader', 'true');
  marker.setAttribute('data-testid', 'adsense-loader-script');
  document.head.appendChild(marker);
  const s = document.createElement('script');
  s.async = true;
  s.crossOrigin = 'anonymous';
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(CLIENT_ID)}`;
  s.onload = () => { window.__adsenseLoaded = true; window.dispatchEvent(new Event('adsense-loaded')); };
  document.head.appendChild(s);
}

export default function AdSenseLoader() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [consentTick, setConsentTick] = useState(0);
  const geoRef = useRef(null);
  const loadedRef = useRef(false);

  // Bump state when the visitor toggles consent so the effect re-evaluates.
  useEffect(() => {
    const handler = () => setConsentTick((n) => n + 1);
    window.addEventListener(CONSENT_EVENT, handler);
    return () => window.removeEventListener(CONSENT_EVENT, handler);
  }, []);

  useEffect(() => {
    if (!CLIENT_ID || loading) return;
    if (isPremiumUser(user)) return;
    if (isExcludedPath(location.pathname)) return;
    if (loadedRef.current) return;

    let cancelled = false;
    (async () => {
      if (!geoRef.current) geoRef.current = await fetchGeoHint();
      if (cancelled) return;
      const requiresConsent = !!geoRef.current?.requires_ad_consent;
      if (requiresConsent && !getConsent().advertising) return; // wait for consent
      injectAdSenseScript();
      loadedRef.current = true;
    })();
    return () => { cancelled = true; };
  }, [user, loading, location.pathname, consentTick]);

  return null;
}
